import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { useAttachmentObjectUrl } from './useAttachmentObjectUrl';

interface LightboxState {
  open: (attachmentId: string, alt?: string) => void;
}

const LightboxContext = createContext<LightboxState | null>(null);

/** Click any attachment image to view it full-size in this overlay. */
export function LightboxProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<{ id: string; alt: string } | null>(null);
  const open = useCallback((attachmentId: string, alt = 'attachment') => setCurrent({ id: attachmentId, alt }), []);

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      {current && <LightboxModal id={current.id} alt={current.alt} onClose={() => setCurrent(null)} />}
    </LightboxContext.Provider>
  );
}

export function useLightbox(): LightboxState {
  const ctx = useContext(LightboxContext);
  // Fall back to a no-op if no provider (keeps components usable in isolation).
  return ctx ?? { open: () => undefined };
}

function LightboxModal({ id, alt, onClose }: { id: string; alt: string; onClose: () => void }) {
  const src = useAttachmentObjectUrl(id);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {src && (
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20"
          >
            <ExternalLink className="w-4 h-4" /> Open in new tab
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex items-center justify-center rounded-md bg-white/10 p-1.5 text-white hover:bg-white/20"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {src ? (
        <img
          src={src}
          alt={alt}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[90vh] max-w-[92vw] object-contain rounded shadow-2xl"
        />
      ) : (
        <div className="text-white/70 text-sm">Loading…</div>
      )}
    </div>
  );
}
